import React, { useState, useEffect } from 'react';
// Framer Motion supprimé - utilisation d'animations CSS
import { X, Save, User, Phone, AlertCircle, FileText } from 'lucide-react';
import Select from 'react-select';
import { useHealthZones } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

const PatientCreateModal = ({ isOpen, onClose, onSave, loading }) => {
  const { data: healthZones = [], loading: healthZonesLoading } = useHealthZones();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    gender: 'M',
    bloodType: '',
    zone: '',
    diagnosis: '',
    status: 'Stable',
    doctor: '',
    medicalHistory: '',
    allergies: '',
    currentMedications: '',
    emergencyContact: '',
    emergencyPhone: '',
    insuranceNumber: ''
  });

  const [selectedZone, setSelectedZone] = useState(null);
  const [errors, setErrors] = useState({});
  const [doctorInfo, setDoctorInfo] = useState(null);
  
  // Récupérer les informations du docteur connecté (nom, spécialité, userId)
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      if (user?.role?.toLowerCase() === 'doctor' && user?.id) {
        try {
          const doctorsList = await apiService.getDoctors().catch(() => ({ data: [] }));
          const doctors = Array.isArray(doctorsList?.data) ? doctorsList.data : [];
          const meAsDoctor = doctors.find((d) => String(d?.user?.id) === String(user?.id));
          
          if (meAsDoctor) {
            setDoctorInfo({
              name: meAsDoctor.user?.name || user?.name || '',
              specialty: meAsDoctor.specialty || meAsDoctor.speciality || 'Non spécifiée',
              userId: user.id
            });
            
            // Afficher le nom, la spécialité et le userId
            const doctorDisplay = `Dr. ${meAsDoctor.user?.name || user?.name || ''} - ${meAsDoctor.specialty || meAsDoctor.speciality || 'Non spécifiée'} (ID: ${user.id})`;
            setFormData(prev => ({
              ...prev,
              doctor: doctorDisplay
            }));
          } else {
            // Si le docteur n'est pas trouvé dans la liste, utiliser juste les infos de l'utilisateur
            setDoctorInfo({
              name: user?.name || '',
              specialty: 'Non spécifiée',
              userId: user.id
            });
            setFormData(prev => ({
              ...prev,
              doctor: `Dr. ${user?.name || ''} - Non spécifiée (ID: ${user.id})`
            }));
          }
        } catch (err) {
          console.error('Erreur lors de la récupération des infos du docteur:', err);
          // En cas d'erreur, utiliser les infos de base
          setDoctorInfo({
            name: user?.name || '',
            specialty: 'Non spécifiée',
            userId: user.id
          });
          setFormData(prev => ({
            ...prev,
            doctor: `Dr. ${user?.name || ''} - Non spécifiée (ID: ${user.id})`
          }));
        }
      }
    };
    
    fetchDoctorInfo();
  }, [user]);
  
  // Préparer les options pour le sélecteur de zones
  const zoneOptions = healthZones.map(zone => ({
    value: zone.name,
    label: `${zone.name} (${zone.code}) - ${zone.province}`
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleZoneChange = (selectedOption) => {
    setSelectedZone(selectedOption);
    setFormData(prev => ({
      ...prev,
      zone: selectedOption ? selectedOption.value : ''
    }));
    
    // Clear error
    if (errors.zone) {
      setErrors(prev => ({
        ...prev,
        zone: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis';
    if (!formData.age || formData.age < 0) newErrors.age = 'L\'âge doit être valide';
    if (!formData.zone.trim()) newErrors.zone = 'La zone est requise';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      age: '',
      gender: 'M',
      bloodType: '',
      zone: '',
      diagnosis: '',
      status: 'Stable',
      doctor: '',
      medicalHistory: '',
      allergies: '',
      currentMedications: '',
      emergencyContact: '',
      emergencyPhone: '',
      insuranceNumber: ''
    });
    setSelectedZone(null);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div>
      {isOpen && (
        <div
          
          
          
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <div
            
            
            
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Nouveau Patient</h2>
                  <p className="text-gray-600">Ajouter un nouveau patient au système</p>
                </div>
              </div>
              <button
                
                
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                disabled={loading}
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Form - Same as edit modal but for creation */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Informations personnelles */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nom du patient"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="email@example.com"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+243 81 000 0000"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Âge *
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      min="0"
                      max="120"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.age ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="45"
                    />
                    {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genre
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="M">Homme</option>
                      <option value="F">Femme</option>
                      <option value="Other">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Groupe sanguin
                    </label>
                    <select
                      name="bloodType"
                      value={formData.bloodType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Informations médicales */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Informations médicales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zone *
                    </label>
                    <Select
                      value={selectedZone}
                      onChange={handleZoneChange}
                      options={zoneOptions}
                      isLoading={healthZonesLoading}
                      isClearable
                      placeholder="Sélectionner une zone de santé..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: errors.zone ? '#ef4444' : '#d1d5db',
                          '&:hover': {
                            borderColor: errors.zone ? '#ef4444' : '#9ca3af',
                          },
                          minHeight: '42px',
                        }),
                      }}
                    />
                    {errors.zone && <p className="text-red-500 text-sm mt-1">{errors.zone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Stable">Stable</option>
                      <option value="En observation">En observation</option>
                      <option value="Critique">Critique</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Diagnostic
                    </label>
                    <input
                      type="text"
                      name="diagnosis"
                      value={formData.diagnosis}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="En cours d'évaluation"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Médecin assigné
                    </label>
                    <input
                      type="text"
                      name="doctor"
                      value={formData.doctor}
                      readOnly
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Dr. Non assigné"
                    />
                  </div>
                </div>
              </div>

              {/* Antécédents médicaux */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  Antécédents médicaux
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Antécédents médicaux
                    </label>
                    <textarea
                      name="medicalHistory"
                      value={formData.medicalHistory}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Antécédents médicaux du patient..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergies
                    </label>
                    <textarea
                      name="allergies"
                      value={formData.allergies}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Allergies connues..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Médicaments actuels
                    </label>
                    <textarea
                      name="currentMedications"
                      value={formData.currentMedications}
                      onChange={handleChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Médicaments en cours..."
                    />
                  </div>
                </div>
              </div>

              {/* Contact d'urgence */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  Contact d'urgence
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du contact
                    </label>
                    <input
                      type="text"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nom du contact d'urgence"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone d'urgence
                    </label>
                    <input
                      type="tel"
                      name="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+243 81 000 0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro d'assurance
                    </label>
                    <input
                      type="text"
                      name="insuranceNumber"
                      value={formData.insuranceNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Numéro d'assurance"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  
                  
                  onClick={handleClose}
                  disabled={loading}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  
                  
                  disabled={loading}
                  className="px-6 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Créer</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientCreateModal;
